import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerCargoDetailsOrdersTableHelper from "./order-optimizer-cargo-details-orders-table.helper";
import OrderOptimizerCargoDetailsOrdersTableRow from "../common/types/order-optimizer-cargo-details-orders-table-row";
import orderOptimizerHelper from "../../../common/order-optimizer.helper";
import uuidService from "../../../../../common/utils/uuid/uuid.service";
import OrderOptimizerDetailsTableDotComponent from "../../../common/dot/order-optimizer-details-orders-table-dot.component";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import TableButtonComponent from "../../../../../common/components/table/button/table-button.component";
import { OrderOptimizerCargoDetailsItemOrders } from "../common/types/order-optimizer-cargo-details-item";
import orderOptimizerCargoDetailsHelper from "../order-optimizer-cargo-details.helper";

type OrderOptimizerCargoDetailsOrdersTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsOrderItems?: OrderOptimizerCargoDetailsItemOrders[];
  hiddenOrders?: number[];
  onOrderVisibilityClick: (id: number) => void;
};

const OrderOptimizerCargoDetailsOrdersTableComponent: FC<
  OrderOptimizerCargoDetailsOrdersTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerCargoDetailsOrdersTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createTableRow = (
    detailsOrderItems: OrderOptimizerCargoDetailsItemOrders,
    index: number,
    hiddenOrders: number[],
    humanIds: number[]
  ): OrderOptimizerCargoDetailsOrdersTableRow => {
    const distanceLabel = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance
    );
    const distanceTitle = orderOptimizerCargoDetailsHelper.getDistanceLabel(
      detailsOrderItems.distance
    );

    const totalTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime
    );
    const totalTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.totalTime
    );

    const haltingTimeLabel = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime
    );
    const haltingTimeTitle = orderOptimizerCargoDetailsHelper.getTimeDeltaLabel(
      detailsOrderItems.haltingTime
    );

    const isButtonVisible =
      props.detailsOrderItems!.length - props.hiddenOrders!.length >= 3 ||
      hiddenOrders.includes(humanIds[index]);
    const isOrderHidden = hiddenOrders.includes(humanIds[index]);
    const dotColor = isOrderHidden
      ? "#808080"
      : orderOptimizerHelper.getOrderColor(index);

    return {
      id: uuidService.generate(),
      className: isOrderHidden
        ? "order_optimizer_cargo_details_orders_table__hidden_row"
        : undefined,
      value: {
        humanId: (
          <div className="order_optimizer_cargo_details_orders_table__humanId">
            <OrderOptimizerDetailsTableDotComponent color={dotColor} />
            <span title={String(detailsOrderItems.humanId)}>
              {detailsOrderItems.humanId}
            </span>
          </div>
        ),
        billingModel: (
          <div title={detailsOrderItems.billingModel}>
            {detailsOrderItems.billingModel}
          </div>
        ),
        taxiCorporation: (
          <div title={detailsOrderItems.taxiCorporation}>
            {detailsOrderItems.taxiCorporation}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: <div title={haltingTimeTitle}>{haltingTimeLabel}</div>,
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        action: (
          <div style={{ marginLeft: "20px" }}>
            <TableButtonComponent
              icon={faEye}
              type={"brand"}
              onClick={() =>
                props.onOrderVisibilityClick(detailsOrderItems.humanId)
              }
              isDisabled={!isButtonVisible}
            />
          </div>
        ),
      },
    };
  };

  const rows: OrderOptimizerCargoDetailsOrdersTableRow[] = useMemo(() => {
    const detailsOrderItems = props.detailsOrderItems || [];
    const humanIds = detailsOrderItems.map((item) => item.humanId);

    return detailsOrderItems.map((item, index) =>
      createTableRow(item, index, props.hiddenOrders ?? [], humanIds)
    );
  }, [props.detailsOrderItems, props.hiddenOrders, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerCargoDetailsOrdersTableComponent;
